import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha, Alert, Box, Card, CardContent, Chip, CircularProgress, Divider, Snackbar, Stack, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSearchParams } from 'react-router-dom';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import { useAuth } from '../../hooks/useAuth';
import { getOrders } from '../../services/orderBilling';
import UpdatePaymentDialog from './UpdatePaymentDialog';

// The org's picklist value is the singular "Automated Collection"; the plural is kept as a
// safety net. (The Payment Info page checks only the plural, so it has this same gap.)
const PAY_NOW_STATUSES = ['Error', 'Automated Collection', 'Automated Collections'];
const isPayNow = (order) => PAY_NOW_STATUSES.includes(order.paymentStatus);

// Orders that share the same physical card (type + last4 + expiry) are grouped together so
// one Update applies to all of them in a single call. Orders with no card stay on their own.
const cardSignature = (order) => (order.paymentMethodDefaultId ? `${order.cardType}|${order.cardLast4}|${order.cardExpirationMonth}|${order.cardExpirationYear}` : `__none_${order.id}`);

const groupByCard = (list) => {
    const map = {};
    list.forEach((o) => {
        const key = cardSignature(o);
        if (!map[key]) {
            map[key] = [];
        }
        map[key].push(o);
    });
    return Object.values(map);
};

// Finalized business name, then 1st/2nd name choices; fall back to the account name.
const displayName = (order) => order.businessName || order.businessAccountName || order.accountName;

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount || 0);

const frequencyLabel = (frequency) => {
    if (frequency === 'Annual') return 'annually';
    if (frequency === 'Monthly') return 'monthly';
    if (frequency === 'Biennial') return 'biennially';
    return frequency;
};

// Intro line shown under the heading. Each email can override it via the `message` URL
// param (e.g. ?orders=...&message=Please%20update%20your%20card); otherwise this default shows.
const DEFAULT_INTRO = 'Review the orders below and update the card on file, or pay any past-due balance.';

// Handled orders (updated or paid) are remembered in localStorage with a TTL so they show as
// "Completed" even if the backend re-fetch hasn't caught up yet (Chargent applies changes
// asynchronously). The marker self-expires so a stale state can't linger if the order later
// needs attention again. Shape: { [orderId]: { at: epochMs, type: 'updated' | 'paid' } }.
const COMPLETED_STORAGE_KEY = 'updateCustomerOrders.completed';
const COMPLETED_TTL_MS = 24 * 60 * 60 * 1000;

const readCompletedMap = () => {
    try {
        const raw = JSON.parse(window.localStorage.getItem(COMPLETED_STORAGE_KEY) || '{}');
        const now = Date.now();
        const fresh = {};
        Object.keys(raw).forEach((id) => {
            const entry = raw[id];
            if (entry && typeof entry.at === 'number' && now - entry.at < COMPLETED_TTL_MS) {
                fresh[id] = entry;
            }
        });
        return fresh;
    } catch {
        return {};
    }
};

function UpdateCustomerOrders() {
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const { getUserDetailsNotAsync } = useAuth();
    const contactId = getUserDetailsNotAsync()?.contact?.id;

    const ordersParam = searchParams.get('orders') || '';
    const orderIds = useMemo(
        () =>
            ordersParam
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
        [ordersParam]
    );

    const introMessage = (searchParams.get('message') || '').trim() || DEFAULT_INTRO;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // { orders, mode: 'update' | 'pay' }
    const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
    const [completedMap, setCompletedMap] = useState(() => readCompletedMap());

    const completedIds = useMemo(() => new Set(Object.keys(completedMap)), [completedMap]);

    const showToast = (severity, message) => setToast({ open: true, severity, message });

    const markCompleted = (ids, type) => {
        if (!ids || ids.length === 0) {
            return;
        }
        setCompletedMap((prev) => {
            const next = { ...prev };
            const now = Date.now();
            ids.forEach((id) => {
                const order = orders.find((o) => o.id === id);
                next[id] = { at: now, type, cardSig: order ? cardSignature(order) : undefined };
            });
            return next;
        });
    };

    const fetchOrders = useCallback(() => {
        if (orderIds.length === 0) {
            setOrders([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        getOrders(orderIds)
            .then((result) => {
                setOrders(result?.isSuccess && Array.isArray(result.data) ? result.data : []);
            })
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    }, [orderIds]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Persist the completed markers whenever they change.
    useEffect(() => {
        try {
            window.localStorage.setItem(COMPLETED_STORAGE_KEY, JSON.stringify(completedMap));
        } catch {
            // ignore storage errors (private mode, quota, etc.)
        }
    }, [completedMap]);

    // Reconcile optimistic markers against fresh backend data: drop the marker if the order is
    // past-due again (needs attention), or — for a card update — once the new card is reflected.
    // A successful payment otherwise stays in Completed until its TTL.
    useEffect(() => {
        if (orders.length === 0) {
            return;
        }
        setCompletedMap((prev) => {
            let changed = false;
            const next = { ...prev };
            Object.keys(prev).forEach((id) => {
                const order = orders.find((o) => o.id === id);
                if (!order) {
                    return;
                }
                const entry = prev[id];
                const confirmed = isPayNow(order) || (entry.type !== 'paid' && entry.cardSig != null && cardSignature(order) !== entry.cardSig);
                if (confirmed) {
                    delete next[id];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [orders]);

    const { payNowOrders, groups, completedGroups } = useMemo(() => {
        const completedList = orders.filter((o) => completedIds.has(o.id));
        const pending = orders.filter((o) => !completedIds.has(o.id));
        const due = pending.filter(isPayNow);
        const updatable = pending.filter((o) => !isPayNow(o));
        return { payNowOrders: due, groups: groupByCard(updatable), completedGroups: groupByCard(completedList) };
    }, [orders, completedIds]);

    const renderCardLine = (order) => {
        if (!order.paymentMethodDefaultId) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No card on file
                </Typography>
            );
        }
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <PaymentIcon type={order.cardType?.toLowerCase()} format="flatRounded" width={28} />
                <Typography variant="body2">{`${order.cardType || 'Card'} ending in ${order.cardLast4}`}</Typography>
                {order.cardExpirationMonth && order.cardExpirationYear && (
                    <Typography variant="body2" sx={{ color: order.ccExpiringSoon ? theme.palette.error.main : 'text.secondary' }}>
                        {`· Exp ${order.cardExpirationMonth}/${order.cardExpirationYear}`}
                    </Typography>
                )}
            </Stack>
        );
    };

    // One card layout shared by the Orders and Completed sections — the right-side `action`
    // is the only difference (Update button vs Completed badge).
    const renderGroupCard = (group, action) => (
        <Card key={cardSignature(group[0])} variant="outlined">
            <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
                    <Box>
                        {renderCardLine(group[0])}
                        {group.length > 1 && (
                            <Typography variant="caption" color="text.secondary">
                                {`${group.length} orders on this card`}
                            </Typography>
                        )}
                    </Box>
                    {action}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={1.25}>
                    {group.map((order) => (
                        <Stack key={order.id} direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="body2" fontWeight="fontWeightSemiBold">
                                    {displayName(order)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {order.paymentTitle} · Recurs {frequencyLabel(order.paymentFrequency)}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2">{formatCurrency(order.chargeAmount)}</Typography>
                                {order.nextScheduledPaymentString && (
                                    <Typography variant="caption" color="text.secondary">
                                        Next: {order.nextScheduledPaymentString}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );

    const doneCount = completedGroups.reduce((n, g) => n + g.length, 0);
    const remainingCount = payNowOrders.length + groups.reduce((n, g) => n + g.length, 0);
    const totalItems = doneCount + remainingCount;

    return (
        <Box sx={{ maxWidth: 760, margin: '0 auto', padding: { xs: '1.5rem 1rem', sm: '2.5rem 2rem' }, zoom: 1.05 }}>
            <Typography
                component="h1"
                variant="heading1"
                color="text.primary"
                fontFamily='"Barlow Condensed", sans-serif'
                fontSize={{ xs: theme.typography.customFontSize.xxxLarge, sm: theme.typography.customFontSize.verylarge }}
            >
                Update Billing Information
            </Typography>
            <Typography component="p" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                {introMessage}
            </Typography>

            {loading ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            ) : orderIds.length === 0 ? (
                <Alert severity="info">This link didn't include any orders to update. Please use the link from your email.</Alert>
            ) : orders.length === 0 ? (
                <Alert severity="info">We couldn't find any matching orders for your account.</Alert>
            ) : (
                <Stack spacing={3}>
                    {/* Progress status — counts both card updates and past-due payments */}
                    {totalItems > 0 && remainingCount === 0 && (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: { xs: 4, sm: 5 },
                                px: 3,
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.success.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
                            <Typography
                                component="h2"
                                variant="heading1"
                                fontFamily='"Barlow Condensed", sans-serif'
                                color="success.main"
                                fontSize={{ xs: theme.typography.customFontSize.xxxLarge, sm: theme.typography.customFontSize.verylarge }}
                                sx={{ mt: 1 }}
                            >
                                You're all set!
                            </Typography>
                            <Typography variant="subHeading" color="text.secondary" sx={{ mt: 0.5 }}>
                                All {totalItems} {totalItems === 1 ? 'order has' : 'orders have'} been taken care of. Thank you!
                            </Typography>
                        </Box>
                    )}

                    {/* Past-due orders — shown individually with a Pay now action */}
                    {payNowOrders.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="fontWeightBold" sx={{ mb: 1 }}>
                                Past due
                            </Typography>
                            <Stack spacing={2}>
                                {payNowOrders.map((order) => (
                                    <Card key={order.id} variant="outlined" sx={{ borderColor: theme.palette.error.main }}>
                                        <CardContent>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="fontWeightSemiBold" sx={{ mb: 0.5 }}>
                                                        {displayName(order)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {order.paymentTitle}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.5 }} color="error.main" fontWeight="fontWeightBold">
                                                        Amount due: {formatCurrency(order.chargeAmount)}
                                                    </Typography>
                                                    <Box sx={{ mt: 1.5 }}>{renderCardLine(order)}</Box>
                                                </Box>
                                                <LoadingButton
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => setDialog({ orders: [order], mode: 'pay' })}
                                                    sx={{ textTransform: 'none', minWidth: 120 }}
                                                >
                                                    Pay now
                                                </LoadingButton>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Active orders — grouped by the card they share */}
                    {groups.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="fontWeightBold" sx={{ mb: 1 }}>
                                Orders
                            </Typography>
                            <Stack spacing={2}>
                                {groups.map((group) =>
                                    renderGroupCard(
                                        group,
                                        <LoadingButton variant="outlined" onClick={() => setDialog({ orders: group, mode: 'update' })} sx={{ textTransform: 'none', minWidth: 120 }}>
                                            {group.length > 1 ? 'Update all' : 'Update'}
                                        </LoadingButton>
                                    )
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Completed — same card layout, with a Completed badge instead of an action */}
                    {completedGroups.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="fontWeightBold" sx={{ mb: 1 }}>
                                Completed
                            </Typography>
                            <Stack spacing={2}>{completedGroups.map((group) => renderGroupCard(group, <Chip color="success" icon={<CheckCircleIcon fontSize="small" />} label="Completed" />))}</Stack>
                        </Box>
                    )}
                </Stack>
            )}

            <UpdatePaymentDialog
                open={Boolean(dialog)}
                mode={dialog?.mode || 'update'}
                orders={dialog?.orders || []}
                contactId={contactId}
                onClose={() => setDialog(null)}
                onToast={showToast}
                onUpdated={(ids, type) => {
                    markCompleted(ids, type);
                    fetchOrders();
                }}
            />

            <Snackbar open={toast.open} autoHideDuration={6000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} onClose={() => setToast((t) => ({ ...t, open: false }))}>
                <Alert severity={toast.severity} variant="filled" onClose={() => setToast((t) => ({ ...t, open: false }))}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default UpdateCustomerOrders;
